import React from 'react';

import { translate } from '../../../base/i18n';
import {
    Icon,
    IconArrowDown,
    IconArrowUp,
    IconCheck,
    IconClose,
    IconExclamationSolid,
    IconInfo,
    IconWarning
} from '../../../base/icons';
import { NOTIFICATION_TYPE } from '../../constants';
import AbstractNotification, {
    type Props
} from '../AbstractNotification';

declare var interfaceConfig: Object;

type State = {

    /**
     * Whether or not the notification body is collapsed.
     */
    collapsed: boolean
};

/**
 * Implements a React {@link Component} to display a notification.
 *
 * @augments Component
 */
class Notification extends AbstractNotification<Props, State> {
    /**
     * Initializes a new {@code Notification} instance.
     *
     * @param {Props} props - Component properties.
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            collapsed: false
        };

        this._onToggleCollapse = this._onToggleCollapse.bind(this);
    }

    /**
     * Toggles the collapse state of the notification.
     *
     * @param {Object} [e] - Event object.
     * @returns {void}
     */
    _onToggleCollapse(e) {
        if (e) {
            e.stopPropagation();
        }
        this.setState(prevState => {
            return {
                collapsed: !prevState.collapsed
            };
        });
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const {
            appearance,
            hideErrorSupportLink,
            isDismissAllowed,
            t,
            title,
            titleArguments,
            titleKey,
            uid
        } = this.props;

        const { collapsed } = this.state;
        const description = this._getDescription();
        const hasDescription = Boolean(description && description.length > 0 && description.some(Boolean));
        const actions = this._mapAppearanceToButtons(hideErrorSupportLink);
        const hasActions = Boolean(actions && actions.length > 0);
        const isExpandable = hasDescription || hasActions;
        const showDismiss = isDismissAllowed !== false;
        const actionIconColor = '#ffffff';

        return (
            <div
                className = { `custom-notification custom-notification--${appearance} ${collapsed ? 'is-collapsed' : ''}` }
                data-testid = { titleKey }
                id = { uid }>
                <div className = 'custom-notification__body'>
                    <div className = 'custom-notification__icon'>
                        {this._mapAppearanceToIcon()}
                    </div>
                    <div className = 'custom-notification__content'>
                        <div className = 'custom-notification__header'>
                            <div
                                className = 'custom-notification__title'
                                onClick = { isExpandable ? this._onToggleCollapse : undefined }
                                role = { isExpandable ? 'button' : undefined }
                                tabIndex = { isExpandable ? 0 : undefined }>
                                {title || t(titleKey, titleArguments)}
                            </div>
                            {isExpandable && (
                                <button
                                    aria-label = { collapsed ? 'Expand' : 'Collapse' }
                                    className = 'custom-notification__toggle'
                                    onClick = { this._onToggleCollapse }
                                    title = { collapsed ? 'Expand' : 'Collapse' }
                                    type = 'button'>
                                    <Icon
                                        color = { actionIconColor }
                                        fillColor = { actionIconColor }
                                        size = { 14 }
                                        src = { collapsed ? IconArrowDown : IconArrowUp } />
                                </button>
                            )}
                            {showDismiss && (
                                <button
                                    aria-label = { t('dialog.close') || 'Close' }
                                    className = 'custom-notification__dismiss'
                                    onClick = { this._onDismissed }
                                    title = { t('dialog.close') || 'Close' }
                                    type = 'button'>
                                    <Icon
                                        color = { actionIconColor }
                                        fillColor = { actionIconColor }
                                        size = { 14 }
                                        src = { IconClose } />
                                </button>
                            )}
                        </div>
                        {!collapsed && (
                            <>
                                {hasDescription && (
                                    <div className = 'custom-notification__description'>
                                        {this._renderDescription()}
                                    </div>
                                )}
                                {this._renderActions(actions)}
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    _getDescription: () => Array<string>;

    _getDescriptionKey: () => string;

    _onDismissed: () => void;

    /**
     * Creates a {@code ReactElement} for displaying the contents of the
     * notification.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderDescription() {
        const description = this._getDescription();

        // the id is used for testing the UI
        return (
            <div data-testid = { this._getDescriptionKey() } >
                { description }
            </div>
        );
    }

    _renderActions(actions) {
        if (!actions.length) {
            return null;
        }

        return (
            <div className = 'custom-notification__actions'>
                {actions.map(action => {
                    const _onActionClick = action.onClick;

                    return (<button
                        className = 'custom-notification__action'
                        key = { action.content }
                        onClick = { _onActionClick }
                        type = 'button'>
                        {action.content}
                    </button>);
                })}
            </div>
        );
    }

    /**
     * Opens the support page.
     *
     * @returns {void}
     * @private
     */
    _onOpenSupportLink() {
        window.open(interfaceConfig.SUPPORT_URL, '_blank', 'noopener');
    }

    /**
     * Creates action button configurations for the notification based on
     * notification appearance.
     *
     * @param {boolean} hideErrorSupportLink - Indicates if the support link
     * should be hidden in the error messages.
     * @private
     * @returns {Object[]}
     */
    _mapAppearanceToButtons(hideErrorSupportLink) {
        switch (this.props.appearance) {
        case NOTIFICATION_TYPE.ERROR: {
            const buttons = [
                {
                    content: this.props.t('dialog.dismiss'),
                    onClick: this._onDismissed
                }
            ];

            if (!hideErrorSupportLink) {
                buttons.push({
                    content: this.props.t('dialog.contactSupport'),
                    onClick: this._onOpenSupportLink
                });
            }

            return buttons;
        }
        case NOTIFICATION_TYPE.WARNING:
            return [
                {
                    content: this.props.t('dialog.Ok'),
                    onClick: this._onDismissed
                }
            ];

        default:
            if (this.props.customActionNameKey && this.props.customActionHandler) {
                // Callers may pass either a single key/handler pair or one array
                // each, with a button per entry (the visitors promotion
                // notification uses the array form for Admit/Reject). Calling an
                // array threw, which is why those buttons did nothing.
                const nameKeys = Array.isArray(this.props.customActionNameKey)
                    ? this.props.customActionNameKey
                    : [ this.props.customActionNameKey ];
                const handlers = Array.isArray(this.props.customActionHandler)
                    ? this.props.customActionHandler
                    : [ this.props.customActionHandler ];

                return nameKeys.reduce((buttons, nameKey, index) => {
                    const handler = handlers[index];

                    if (nameKey && typeof handler === 'function') {
                        buttons.push({
                            content: this.props.t(nameKey),
                            onClick: () => {
                                if (handler()) {
                                    this._onDismissed();
                                }
                            }
                        });
                    }

                    return buttons;
                }, []);
            }

            return [];
        }
    }

    /**
     * Creates an icon component depending on the configured notification
     * appearance.
     *
     * @private
     * @returns {ReactElement}
     */
    _mapAppearanceToIcon() {
        const appearance = this.props.appearance;

        switch (appearance) {
        case NOTIFICATION_TYPE.ERROR:
            return (
                <Icon
                    color = '#ffffff'
                    fillColor = '#ffffff'
                    size = { 20 }
                    src = { IconExclamationSolid } />
            );

        case NOTIFICATION_TYPE.WARNING:
            return (
                <Icon
                    color = '#ffffff'
                    fillColor = '#ffffff'
                    size = { 20 }
                    src = { IconWarning } />
            );

        case NOTIFICATION_TYPE.SUCCESS:
            return (
                <Icon
                    color = '#ffffff'
                    fillColor = '#ffffff'
                    size = { 20 }
                    src = { IconCheck } />
            );

        default:
            return (
                <Icon
                    color = '#ffffff'
                    fillColor = '#ffffff'
                    size = { 20 }
                    src = { IconInfo } />
            );
        }
    }
}

export default translate(Notification);


